import './CheckoutPreview.scss';

import { AllFormData, amountKeys } from '../../../types/givingCheckoutForm';
import React, { FunctionComponent, useMemo } from 'react';
import {
  formatCurrency,
  formatCurrencySymbol,
} from '../../utils/currencyFormatting';

import EyeIcon from './eyeIcon.svg';
import PreviewBlock from './PreviewBlock';
import classNames from 'classnames';
import { useCharityData } from '../../context/charityContext';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';

type CheckoutPreviewProps = {
  suggestedAmountDefault?: string;
  previewBlock?: PreviewElements[];
};

type PreviewElements =
  | 'cta'
  | 'frequency'
  | 'suggestedamounts'
  | 'input'
  | 'description';

const CheckoutPreview: FunctionComponent<CheckoutPreviewProps> = ({
  previewBlock,
  suggestedAmountDefault,
}) => {
  const { t } = useTranslation();
  const { charity } = useCharityData();

  const [
    donationFrequency,
    numberOfCustomAmounts,
    suggestedAmounts,
    formSuggestedAmountDefault,
    customAmountDescriptionsEnabled,
    amountDescriptions,
  ] = useWatch<
    AllFormData,
    [
      'donationFrequency',
      'numberOfCustomAmounts',
      'suggestedAmounts',
      'suggestedAmountDefault',
      'customAmountDescriptionsEnabled',
      'amountDescriptions',
    ]
  >({
    name: [
      'donationFrequency',
      'numberOfCustomAmounts',
      'suggestedAmounts',
      'suggestedAmountDefault',
      'customAmountDescriptionsEnabled',
      'amountDescriptions',
    ],
  });

  const suggestedAmountDefaultIndex =
    Number(suggestedAmountDefault ?? formSuggestedAmountDefault) - 1;

  const currencySymbol = formatCurrencySymbol(charity?.settlementCurrencyCode);

  const values = useMemo(() => {
    const amounts = [
      suggestedAmounts.amount1,
      suggestedAmounts.amount2,
      suggestedAmounts.amount3,
      suggestedAmounts.amount4,
      suggestedAmounts.amount5,
    ].splice(0, Number(numberOfCustomAmounts)) as string[];

    const defaultAmount = amounts[suggestedAmountDefaultIndex];

    const amountItems = amounts.map((amount, i) => {
      if (!amount) return undefined;
      const isActive = i === suggestedAmountDefaultIndex;

      return (
        <li
          className={classNames('checkout-preview__radioList__item', {
            'is-active': isActive,
          })}
          key={`preview${i}`}
          data-qa="preview-amount-options"
        >
          <PreviewBlock
            isActive={previewBlock?.includes('suggestedamounts')}
            isDarkBackground={isActive}
          >
            {formatCurrency({
              currencyCode: charity?.settlementCurrencyCode,
              amount: parseInt(amount),
            })}
          </PreviewBlock>
        </li>
      );
    });

    return { defaultAmount, amountItems };
  }, [
    suggestedAmounts,
    numberOfCustomAmounts,
    suggestedAmountDefaultIndex,
    previewBlock,
    charity?.settlementCurrencyCode,
  ]);

  const currencyCode = charity?.settlementCurrencyCode ?? 'GBP';

  const activeDescription =
    amountDescriptions?.[
      `amount${suggestedAmountDefaultIndex + 1}` as amountKeys
    ];
  const hasDescriptionToDisplay =
    customAmountDescriptionsEnabled === 'yes' && Boolean(activeDescription);

  return (
    <>
      <div className="checkout-preview-icon" aria-hidden>
        <img src={EyeIcon} alt="" />
        <span>{t('preview.preview')}</span>
      </div>
      <div aria-hidden className="checkout-preview">
        <div
          className={classNames({ 'jg-hidden': charity?.isMonthlyDisabled })}
        >
          <ul className="checkout-preview__radioList">
            <li
              className={classNames('checkout-preview__radioList__item', {
                'is-active': donationFrequency === 'oneOff',
              })}
            >
              <PreviewBlock
                isActive={previewBlock?.includes('frequency')}
                isDarkBackground={donationFrequency === 'oneOff'}
              >
                {t('common.oneOffLabel')}
              </PreviewBlock>
            </li>
            <li
              className={classNames('checkout-preview__radioList__item', {
                'is-active': donationFrequency === 'monthly',
              })}
            >
              <PreviewBlock
                isActive={previewBlock?.includes('frequency')}
                isDarkBackground={donationFrequency === 'monthly'}
              >
                {t('common.monthlyLabel')}
              </PreviewBlock>
            </li>
          </ul>
        </div>

        <div
          className={classNames({
            'donation-descriptions': customAmountDescriptionsEnabled === 'yes',
          })}
        >
          <div>
            <ul className="checkout-preview__radioList">
              {values.amountItems}
            </ul>
          </div>
          <div>
            {customAmountDescriptionsEnabled === 'yes' && (
              <div
                data-qa="description-preview"
                className="donation-descriptions__description"
              >
                {hasDescriptionToDisplay ? (
                  <PreviewBlock
                    isActive={previewBlock?.includes('description')}
                    isLarge
                  >
                    {activeDescription}
                  </PreviewBlock>
                ) : (
                  <PreviewBlock isLarge isActive>
                    Placeholder description
                  </PreviewBlock>
                )}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="checkout-preview__input">
            <div className="checkout-preview__input__currency">
              <PreviewBlock isActive={previewBlock?.includes('input')}>
                {currencySymbol}
              </PreviewBlock>
            </div>
            <div className="checkout-preview__input__amount">
              <PreviewBlock isActive={previewBlock?.includes('input')}>
                {values.defaultAmount}
              </PreviewBlock>
            </div>

            <div className="checkout-preview__input__currencyCode">
              <PreviewBlock isActive={previewBlock?.includes('input')}>
                {currencyCode}
              </PreviewBlock>
            </div>
          </div>
        </div>
        <div>
          <div className="checkout-preview__cta">
            <PreviewBlock
              isActive={previewBlock?.includes('cta')}
              isDarkBackground
            >
              {t('common.continue')}
            </PreviewBlock>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPreview;
